import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { NotificationProvider } from '@/components/ui/NotificationSystem'
import {
  queryPersister,
  shouldDehydrateQuery,
  PERSIST_MAX_AGE,
  PERSIST_BUSTER,
} from '@/lib/queryPersister'

dayjs.extend(utc)
dayjs.extend(timezone)
import { GlobalOrganizationProvider } from '@/hooks/useGlobalOrganization'
import { GlobalCompanyProvider } from '@/hooks/useGlobalCompany'
import { PermissionsProvider } from '@/hooks/usePermissions'
import { SubscriptionProvider } from '@/hooks/useSubscription'
import { VoiceCallProvider } from '@/providers/VoiceCallProvider'
import { MantineProvider } from '@mantine/core'
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 1 * 60 * 1000, // 1 minute
        // Keep persisted queries in cache long enough that the persister isn't
        // garbage-collected out from under us before rehydrate runs.
        gcTime: PERSIST_MAX_AGE,
      },
    },
  }))

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: PERSIST_MAX_AGE,
        buster: PERSIST_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery },
      }}
    >
      {/*
        TODO(ui-consistency): MantineProvider has no theme and there is no MUI ThemeProvider, so
        Mantine renders its default font + Mantine blue and MUI renders Roboto + #1976d2, against
        the app's Inter + primary-500 (#3b82f6). Cheap win: pass
        `theme={{ fontFamily: 'Inter, system-ui, sans-serif', primaryColor: 'blue' }}` here and a
        minimal MUI createTheme, which fixes most of the visible mismatch.

        TODO(datepickers): three date-picker libraries ship today — react-datepicker
        (ui/DateTimeRangePicker), @mantine/dates (DateFilterModal) and @mui/x-date-pickers
        (LocalizationProvider below, ui/gentleDateTimePicker) — so users meet three different
        calendar designs and the bundle carries all three. Consolidating means rewriting date UI
        inside the payment, job and filter flows, so it is real work rather than a config change;
        @mantine/dates is the natural target since MantineProvider is already mounted.
      */}
      <MantineProvider>
        <NotificationProvider>
          <GlobalOrganizationProvider>
            <GlobalCompanyProvider>
              <SubscriptionProvider>
                <PermissionsProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <VoiceCallProvider>
                      {children}
                    </VoiceCallProvider>
                  </LocalizationProvider>
                </PermissionsProvider>
              </SubscriptionProvider>
            </GlobalCompanyProvider>
          </GlobalOrganizationProvider>
        </NotificationProvider>
      </MantineProvider>
    </PersistQueryClientProvider>
  )
}
